import { parisMinutesOfDay } from './time-utils';
import { HistoryRange } from '../models/monitoring.models';

export type HistoryDay = 'today' | 'yesterday';

/** Passage possible sur 2 jours : le jour même (défaut) et la veille, pas plus. */
export const HISTORY_DAY_OPTIONS: { value: HistoryDay; label: string }[] = [
  { value: 'today', label: "Aujourd'hui" },
  { value: 'yesterday', label: 'Hier' },
];

/** 24 h réparties en 4 plages fixes de 6 h (alignées sur le back). */
export const FIXED_HISTORY_RANGES = [
  { label: '18h00 - 24h00', startMinutes: 1080, endMinutes: 1440 },
  { label: '12h00 - 18h00', startMinutes: 720, endMinutes: 1080 },
  { label: '6h00 - 12h00', startMinutes: 360, endMinutes: 720 },
  { label: '0h00 - 6h00', startMinutes: 0, endMinutes: 360 },
] as const;

export function parseHistoryDay(value: string | null): HistoryDay {
  return value === 'yesterday' ? 'yesterday' : 'today';
}

/**
 * Plage par défaut pour aujourd'hui : celle qui contient l'heure courante en France
 * (Europe/Paris, DST-aware) — les libellés (« 18h00 - 24h00 », etc.) sont l'heure que lit
 * l'ASCT sur le terrain, pas l'heure UTC : comparer en UTC décale la sélection d'1h à 2h
 * selon la saison, voire fait rater le changement de jour côté Paris (ex. 22h30 UTC = déjà
 * 0h30 le lendemain à Paris en été).
 */
export function defaultRangeLabelForToday(now: Date = new Date()): string {
  const nowMinutes = parisMinutesOfDay(now);
  const range =
    FIXED_HISTORY_RANGES.find(
      (r) => r.startMinutes <= nowMinutes && nowMinutes < r.endMinutes,
    ) ?? FIXED_HISTORY_RANGES[FIXED_HISTORY_RANGES.length - 1];
  return range.label;
}

/** Plage par défaut pour hier : matinée 6h–12h. */
export function defaultRangeLabelForYesterday(): string {
  return '6h00 - 12h00';
}

export function defaultRangeLabelForDay(day: HistoryDay): string {
  return day === 'yesterday' ? defaultRangeLabelForYesterday() : defaultRangeLabelForToday();
}

/** Gère les plages à cheval sur minuit (ex. « 20h00 - 02h00 », startMinutes > endMinutes). */
export function rangeContainsMinute(
  range: Pick<HistoryRange, 'startMinutes' | 'endMinutes'>,
  minute: number,
): boolean {
  const { startMinutes, endMinutes } = range;
  return startMinutes <= endMinutes
    ? minute >= startMinutes && minute < endMinutes
    : minute >= startMinutes || minute < endMinutes;
}

/**
 * Plage courante parmi celles renvoyées par le back : celle dont la fenêtre
 * [startMinutes, endMinutes) — en minutes UTC de la journée, comme le reste des données du
 * back — contient l'heure actuelle. Ne s'applique qu'à « aujourd'hui » (pour « hier », il
 * n'y a pas de « maintenant » pertinent). On ne présume plus de bornes fixes côté IHM
 * (elles peuvent différer de celles réellement envoyées par le back — ex. plages calées sur
 * 2h/8h/14h/20h heure de Paris plutôt que 0h/6h/12h/18h) : on cherche directement dans la
 * liste reçue plutôt que de comparer des libellés, ce qui faisait échouer la sélection et
 * retombait à tort sur la première plage de la liste (souvent déjà terminée).
 */
export function currentRangeFor<T extends Pick<HistoryRange, 'startMinutes' | 'endMinutes'>>(
  ranges: T[],
  day: HistoryDay,
  now: Date = new Date(),
): T | undefined {
  if (day !== 'today') {
    return undefined;
  }
  const nowMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
  return ranges.find((r) => rangeContainsMinute(r, nowMinutes));
}

/** Minimal slot shape for default selection. */
export interface HistorySlotPick {
  minutes: number;
  color: string;
}

/**
 * Créneau par défaut à la ouverture de l'écran « État de la gare » :
 * le quart d'heure en cours (UTC, comme les données du back), sinon le dernier créneau
 * passé non gris.
 */
export function defaultHistorySlotMinutes(
  slots: HistorySlotPick[],
  now: Date = new Date(),
): number | null {
  if (slots.length === 0) {
    return null;
  }

  const minutes = now.getUTCHours() * 60 + now.getUTCMinutes();
  const nowSlot = Math.floor(minutes / 15) * 15;

  const exact = slots.find((s) => s.minutes === nowSlot && s.color !== 'grey');
  if (exact) {
    return exact.minutes;
  }

  const latestBeforeNow = slots.find((s) => s.minutes <= nowSlot && s.color !== 'grey');
  if (latestBeforeNow) {
    return latestBeforeNow.minutes;
  }

  return slots.find((s) => s.color !== 'grey')?.minutes ?? slots[0]?.minutes ?? null;
}
